import { HttpClient, HttpContext, HttpDownloadProgressEvent, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AssistantConversationList,
  AssistantDoneEvent,
  AssistantMessage,
  AssistantTranscript,
} from '../models/assistant.model';

/**
 * Razgovor sa Ratkom (AI asistentom). Servis živi u root-u da bi panel preživeo
 * navigaciju između stranica; istoriju razgovora drži server (conversationId),
 * ovde se čuva samo ono što se prikazuje.
 *
 * Endpoint odgovara kao text/event-stream. Kao i analizator dokumenata, čita se
 * kroz HttpClient (reportProgress + responseType text) da bi TokenInterceptor
 * dodao JWT; parser seče po praznoj liniji između SSE događaja.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  /** Zaglavlje po kome TokenInterceptor preskače indikator zauzetosti za ovaj dugotrajni zahtev. */
  static readonly STREAM_HEADER = 'X-Assistant-Stream';

  private readonly apiURL = environment.apiURL;
  private readonly messagesSubject = new BehaviorSubject<Array<AssistantMessage>>([]);
  private readonly streamingSubject = new BehaviorSubject<boolean>(false);
  private readonly openSubject = new BehaviorSubject<boolean>(false);

  messages$ = this.messagesSubject.asObservable();
  streaming$ = this.streamingSubject.asObservable();
  open$ = this.openSubject.asObservable();

  private conversationId: string | null = null;
  private request: Subscription | null = null;

  constructor(private http: HttpClient) {}

  get isStreaming(): boolean {
    return this.streamingSubject.value;
  }

  setOpen(open: boolean): void {
    this.openSubject.next(open);
  }

  toggle(): void {
    this.openSubject.next(!this.openSubject.value);
  }

  /** Počinje novi razgovor: server dobija novi conversationId na prvoj poruci. */
  newConversation(): void {
    this.stop();
    this.conversationId = null;
    this.messagesSubject.next([]);
  }

  /** Koji je razgovor trenutno otvoren; null dok prva poruka nije poslata. */
  get openConversationId(): string | null {
    return this.conversationId;
  }

  /** Prethodni razgovori prijavljenog korisnika, najnoviji prvi. */
  listConversations(): Observable<AssistantConversationList> {
    return this.http.get<AssistantConversationList>(`${this.apiURL}/assistant/conversations`);
  }

  deleteConversation(conversationId: string): Observable<unknown> {
    return this.http.delete(`${this.apiURL}/assistant/conversations/${conversationId}`);
  }

  /**
   * Otvara raniji razgovor: učita transkript i veže panel za taj conversationId,
   * pa sledeća poruka nastavlja isti razgovor umesto da otvori novi.
   */
  openConversation(conversationId: string): Observable<AssistantTranscript> {
    this.stop();
    return this.http.get<AssistantTranscript>(`${this.apiURL}/assistant/conversations/${conversationId}`).pipe(
      tap(transcript => {
        this.conversationId = transcript.conversationId;
        this.messagesSubject.next(
          transcript.messages.map(message => ({ ...message, steps: [], streaming: false }))
        );
      })
    );
  }

  /**
   * Čisti panel ako je otvoren baš obrisani razgovor; inače ga ne dira, jer je
   * korisnik obrisao neki drugi red iz liste.
   */
  forgetIfOpen(conversationId: string): void {
    if (this.conversationId === conversationId) {
      this.newConversation();
    }
  }

  /** Prekida odgovor u toku; server na prekid veze zaustavlja i poziv modela. */
  stop(): void {
    if (!this.request) {
      return;
    }
    this.request.unsubscribe();
    this.request = null;
    this.patchLast(message => ({ ...message, streaming: false }));
    this.streamingSubject.next(false);
  }

  send(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || this.isStreaming) {
      return;
    }

    this.messagesSubject.next([
      ...this.messagesSubject.value,
      { role: 'user', text: trimmed, steps: [], streaming: false },
      { role: 'assistant', text: '', steps: [], streaming: true },
    ]);
    this.streamingSubject.next(true);

    // partialText je kumulativan, pa se obrađuje samo deo posle `consumed`
    let consumed = 0;
    let buffer = '';

    const processText = (text: string, isFinal = false) => {
      buffer += text.slice(consumed);
      consumed = text.length;

      const frames = buffer.split('\n\n');
      buffer = isFinal ? '' : frames.pop() || '';
      frames.filter(frame => frame.trim()).forEach(frame => this.handleFrame(frame));
    };

    const headers = new HttpHeaders({ [AssistantService.STREAM_HEADER]: '1' });
    const context = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);

    this.request = this.http
      .post(`${this.apiURL}/assistant/chat`, { message: trimmed, conversationId: this.conversationId || undefined }, {
        headers,
        context,
        observe: 'events',
        responseType: 'text',
        reportProgress: true,
      })
      .subscribe({
        next: (event: HttpEvent<string>) => {
          if (event.type === HttpEventType.DownloadProgress) {
            processText((event as HttpDownloadProgressEvent).partialText || '');
          } else if (event.type === HttpEventType.Response) {
            processText(event.body || '', true);
            this.finish();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.fail(this.extractErrorMessage(err));
        },
      });
  }

  /** Jedan SSE okvir: linija `event: x` pa jedna ili više `data:` linija. */
  private handleFrame(frame: string): void {
    let event = 'message';
    const dataLines: Array<string> = [];

    frame.split('\n').forEach(line => {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    });
    if (!dataLines.length) {
      return;
    }

    let data: any;
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      return;
    }

    switch (event) {
      case 'conversation':
        this.conversationId = data.conversationId;
        break;
      case 'status':
        this.patchLast(message => ({ ...message, steps: [...message.steps, { tool: data.tool, message: data.message }] }));
        break;
      case 'text':
        this.patchLast(message => ({ ...message, text: message.text + (data.delta || '') }));
        break;
      case 'done':
        this.conversationId = (data as AssistantDoneEvent).conversationId || this.conversationId;
        break;
      case 'error':
        this.patchLast(message => ({ ...message, error: data.message }));
        break;
      default:
        break;
    }
  }

  private finish(): void {
    this.request = null;
    this.patchLast(message => ({ ...message, streaming: false }));
    this.streamingSubject.next(false);
  }

  private fail(errorMessage: string): void {
    this.request = null;
    this.patchLast(message => ({ ...message, streaming: false, error: errorMessage }));
    this.streamingSubject.next(false);
  }

  private patchLast(patch: (message: AssistantMessage) => AssistantMessage): void {
    const messages = [...this.messagesSubject.value];
    const last = messages.length - 1;
    if (last < 0 || messages[last].role !== 'assistant') {
      return;
    }
    messages[last] = patch(messages[last]);
    this.messagesSubject.next(messages);
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 401) {
      return 'Nemate pristup asistentu.';
    }
    if (err.status === 0) {
      return 'Veza sa serverom je prekinuta. Pokušajte ponovo.';
    }
    let body: any = err.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = null;
      }
    }
    return body?.description || body?.message || 'Došlo je do greške. Pokušajte ponovo za nekoliko trenutaka.';
  }
}
